import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { AutomationBatchTargetListEntity } from './automation-batch-target-list.entity';
import { AutomationBatchEntity } from './automation-batch.entity';
import { AutomationStepExecutionEntity } from './automation-step-execution.entity';
import { AutomationStepEntity } from './automation-step.entity';
import { AutomationTriggerEntity } from './automation-trigger.entity';
import { AutomationVersionEntity } from './automation-version.entity';
import { BaseEntity } from './base.entity';

@Entity('_automation_execution')
export class AutomationExecutionEntity extends BaseEntity {
	@Column({
		name: '_fk__automation_version__id',
		type: 'int',
		unsigned: true,
	})
	public versionId!: number;

	/**
	 * `cancelled` (PD-14996) is a run an admin stopped, today only a search
	 * index rebuild. It is final: the engine skips the run's remaining steps
	 * and never moves it on to completed or failed. Database sync does not
	 * alter an existing enum column, so the value is added to live tenants by
	 * the AddAutomationExecutionCancelledStatus migration.
	 */
	@Column({
		name: '_execution_status',
		type: 'enum',
		enum: [
			'pending',
			'active',
			'completed',
			'failed',
			'paused',
			'batch_pending',
			'batch_cancelled',
			'excluded',
			'cancelled',
		],
	})
	public executionStatus!:
		| 'pending'
		| 'active'
		| 'completed'
		| 'failed'
		| 'paused'
		| 'batch_pending'
		| 'batch_cancelled'
		| 'excluded'
		| 'cancelled';

	@Column({
		name: '_started_at',
		type: 'timestamp',
		default: () => 'CURRENT_TIMESTAMP',
	})
	public startedAt!: Date | null;

	@Column({ name: '_completed_at', type: 'timestamp', nullable: true })
	public completedAt?: Date;

	@Column({
		name: '_fk__automation_step__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public currentStepId?: number;

	@Column({ name: '_context_data', type: 'json', nullable: true })
	public contextData?: Record<string, any>;

	@Column({ name: '_error_message', type: 'text', nullable: true })
	public errorMessage?: string;

	@Column({ name: '_excluded_reason', type: 'text', nullable: true })
	public excludedReason?: string;

	// Generic entity tracking fields
	@Column({
		name: '_entity_type',
		type: 'varchar',
		length: 50,
		nullable: true,
	})
	public entityType?: string;

	@Column({
		name: '_entity_id',
		type: 'varchar',
		length: 255,
		nullable: true,
	})
	public entityId?: string;

	@Column({ name: '_entity_data', type: 'json', nullable: true })
	public entityData?: Record<string, any>;

	// FK to automation trigger (if triggered)
	@Column({
		name: '_fk__automation_trigger__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public triggerId?: number;

	// FK to batch (if from batch)
	@Column({
		name: '_fk__automation_batch__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public batchId?: number;

	// FK to batch target list (if from batch)
	@Column({
		name: '_fk__automation_batch_target_list__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public batchTargetListId?: number;

	/**
	 * De-duplication key generated from mapped entity data.
	 * SHA-256 hash of batchId + sorted deduplication field values.
	 * Used with unique constraint to prevent duplicate executions within a batch.
	 */
	@Column({
		name: '_deduplication_key',
		type: 'varchar',
		length: 64,
		nullable: true,
	})
	public deduplicationKey?: string;

	// Queue ownership for isolation between systems
	@Column({ name: '_queue_name', type: 'varchar', length: 255, nullable: true })
	public queueName?: string;

	/**
	 * Claim ID for atomic batch processing.
	 * Set to a unique UUID when a worker claims rows for processing.
	 * Prevents race conditions when multiple workers process pending executions simultaneously.
	 */
	@Column({
		name: '_claim_id',
		type: 'varchar',
		length: 36,
		nullable: true,
	})
	public claimId?: string;

	@ManyToOne(() => AutomationVersionEntity, (version) => version.executions)
	@JoinColumn({ name: '_fk__automation_version__id' })
	public version!: AutomationVersionEntity;

	@ManyToOne(() => AutomationTriggerEntity, { nullable: true })
	@JoinColumn({ name: '_fk__automation_trigger__id' })
	public trigger?: AutomationTriggerEntity;

	@ManyToOne(() => AutomationBatchEntity, { nullable: true })
	@JoinColumn({ name: '_fk__automation_batch__id' })
	public batch?: AutomationBatchEntity;

	@ManyToOne(() => AutomationBatchTargetListEntity, { nullable: true })
	@JoinColumn({ name: '_fk__automation_batch_target_list__id' })
	public batchTargetList?: AutomationBatchTargetListEntity;

	@ManyToOne(() => AutomationStepEntity, { nullable: true })
	@JoinColumn({ name: '_fk__automation_step__id' })
	public currentStep?: AutomationStepEntity;

	@OneToMany(
		() => AutomationStepExecutionEntity,
		(stepExecution) => stepExecution.execution,
	)
	public stepExecutions!: Array<AutomationStepExecutionEntity>;

	@Index('idx_automation_execution_version')
	public static versionIndex: [string] = ['versionId'];

	@Index('idx_automation_execution_status')
	public static statusIndex: [string] = ['executionStatus'];

	@Index('idx_automation_execution_current_step')
	public static currentStepIndex: [string] = ['currentStepId'];

	@Index('idx_automation_execution_trigger')
	public static triggerIndex: [string] = ['triggerId'];

	@Index('idx_automation_execution_entity')
	public static entityIndex: [string, string] = ['entityType', 'entityId'];

	@Index('idx_automation_execution_batch')
	public static batchIndex: [string] = ['batchId'];

	@Index('idx_automation_execution_batch_target_list')
	public static batchTargetListIndex: [string] = ['batchTargetListId'];

	/**
	 * Unique constraint for de-duplication within a batch.
	 * Prevents duplicate executions when multiple sources contain the same logical entity.
	 */
	@Index('uk_automation_execution_batch_dedup', { unique: true })
	public static batchDedupIndex: [string, string] = [
		'batchId',
		'deduplicationKey',
	];

	@Index('idx_automation_execution_status_queue')
	public static statusQueueIndex: [string, string] = [
		'executionStatus',
		'queueName',
	];

	@Index('idx_automation_execution_batch_entity_status')
	public static batchEntityStatusIndex: [string, string, string] = [
		'batchId',
		'entityId',
		'executionStatus',
	];

	@Index('idx_automation_execution_trigger_concurrency')
	public static triggerConcurrencyIndex: [string, string, string] = [
		'triggerId',
		'entityId',
		'executionStatus',
	];

	@Index('idx_automation_execution_claim')
	public static claimIndex: [string] = ['claimId'];
}
